import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Trash2, Layers, Sparkles } from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import IndustryCuratorModal from "@/components/IndustryCuratorModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { industryLibList, industryLibUpsert, industryLibDelete } from "@/lib/api";

export default function AdminIndustry() {
  const [industries, setIndustries] = useState([]);
  const [hints, setHints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterIndustry, setFilterIndustry] = useState("__all__");
  const [editing, setEditing] = useState(null); // {industry_slug, industry_label, department, content}
  const [curatorOpen, setCuratorOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const d = await industryLibList(
        filterIndustry === "__all__" ? undefined : filterIndustry,
      );
      setIndustries(d.industries || []);
      setHints(d.hints || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando la biblioteca.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndustry]);

  const startNew = () => {
    setEditing({
      industry_slug: industries[0]?.slug || "telecom",
      industry_label: industries[0]?.label || "",
      department: "",
      content: "",
    });
  };

  const startEdit = (h) => setEditing({ ...h });

  const save = async () => {
    if (!editing.department.trim() || !editing.content.trim()) {
      toast.error("Departamento y contenido son obligatorios.");
      return;
    }
    try {
      await industryLibUpsert(editing);
      toast.success("Conocimiento guardado.");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando.");
    }
  };

  const del = async (h) => {
    if (!window.confirm(`Eliminar el conocimiento de ${h.industry_label} / ${h.department}?`)) return;
    try {
      await industryLibDelete(h.id);
      toast.success("Eliminado.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error eliminando.");
    }
  };

  const grouped = useMemo(() => {
    const m = {};
    for (const h of hints) {
      m[h.industry_label] = m[h.industry_label] || [];
      m[h.industry_label].push(h);
    }
    return m;
  }, [hints]);

  return (
    <div className="min-h-screen bg-white text-ink">
      <TopNav />
      <div className="mx-auto max-w-6xl px-6 py-12 sm:py-16">
        <p className="eyebrow flex items-center gap-1.5">
          <Layers className="h-3 w-3" /> Administración · Biblioteca de Industrias
        </p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">
          Conocimiento experto por sector
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Hints que el <strong>Agente Especializado</strong> usa durante la entrevista ABC y los
          agentes generados heredan en su <code className="font-mono text-[12px] text-accent-blue">system_instruction</code>.
          Funciona como una base compartida entre clientes del mismo sector (regulación, KPIs,
          sistemas, vocabulario).
        </p>

        <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Filtrar por industria</label>
            <div className="mt-1">
              <Select value={filterIndustry} onValueChange={setFilterIndustry}>
                <SelectTrigger className="h-9 w-72 rounded-sm border-slate-300" data-testid="industry-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas las industrias</SelectItem>
                  {industries.map((i) => (
                    <SelectItem key={i.slug} value={i.slug}>{i.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={startNew} className="rounded-sm bg-ink text-[11px] uppercase tracking-[0.2em] text-white hover:bg-ink-2"
                    data-testid="add-industry-hint-btn">
              + Agregar conocimiento
            </Button>
            <Button onClick={() => setCuratorOpen(true)}
                    className="rounded-sm bg-gradient-to-r from-accent-blue to-accent-purple text-[11px] uppercase tracking-[0.2em] text-white hover:opacity-95"
                    data-testid="open-curator-btn">
              <Sparkles className="mr-2 h-3.5 w-3.5" /> Curator Mode
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="mt-10 space-y-10">
            {Object.entries(grouped).map(([industry, list]) => (
              <section key={industry}>
                <h2 className="font-display text-2xl text-ink">{industry}</h2>
                <div className="mt-4 grid gap-3 lg:grid-cols-2">
                  {list.map((h) => (
                    <article key={h.id} className="border border-slate-200 bg-white p-5"
                             data-testid={`hint-card-${h.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-display text-lg font-medium text-ink">{h.department}</p>
                        <div className="flex items-center gap-1">
                          {h.seeded && (
                            <span className="rounded-sm border border-accent-blue/40 bg-accent-blue/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-accent-blue">
                              seed
                            </span>
                          )}
                          <button onClick={() => startEdit(h)} className="text-[11px] text-accent-blue hover:underline"
                                  data-testid={`edit-${h.id}`}>editar</button>
                          <button onClick={() => del(h)} className="ml-2 text-[11px] text-red-600 hover:underline"
                                  data-testid={`delete-${h.id}`}>eliminar</button>
                        </div>
                      </div>
                      <p className="mt-3 whitespace-pre-wrap text-[12px] leading-relaxed text-slate-600">
                        {h.content}
                      </p>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-6">
            <div className="w-full max-w-2xl rounded-sm bg-white p-8 shadow-2xl">
              <p className="eyebrow">{editing.id ? "Editar" : "Nuevo"} conocimiento</p>
              <h3 className="mt-1 font-display text-2xl text-ink">Industria × Departamento</h3>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Industria</label>
                  <Select
                    value={editing.industry_slug}
                    onValueChange={(v) => {
                      const i = industries.find((x) => x.slug === v);
                      setEditing({ ...editing, industry_slug: v, industry_label: i?.label || v });
                    }}
                  >
                    <SelectTrigger className="mt-1 h-9 rounded-sm border-slate-300" data-testid="edit-industry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((i) => (
                        <SelectItem key={i.slug} value={i.slug}>{i.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Departamento</label>
                  <Input
                    value={editing.department}
                    onChange={(e) => setEditing({ ...editing, department: e.target.value })}
                    className="mt-1 rounded-sm border-slate-300"
                    placeholder="Legal, Riesgos, Atención al Cliente, …"
                    data-testid="edit-department-input"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Conocimiento experto</label>
                <Textarea
                  value={editing.content}
                  onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  rows={10}
                  className="mt-1 rounded-sm border-slate-300 font-mono text-[12px] leading-relaxed"
                  placeholder="Vocabulario nativo del sector: regulación, sistemas típicos, KPIs, integraciones, retos comunes…"
                  data-testid="edit-content-textarea"
                />
                <p className="mt-1 text-[10px] text-slate-500">
                  {editing.content.length} caracteres
                </p>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <Button variant="outline" onClick={() => setEditing(null)}
                        className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.18em]">
                  Cancelar
                </Button>
                <Button onClick={save}
                        className="rounded-sm bg-accent-blue text-[11px] uppercase tracking-[0.18em] text-white hover:bg-accent-blue-light"
                        data-testid="save-hint-btn">
                  <Save className="mr-2 h-3.5 w-3.5" /> Guardar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <IndustryCuratorModal
        open={curatorOpen}
        onClose={() => setCuratorOpen(false)}
        onSaved={load}
        defaultIndustries={industries}
      />
    </div>
  );
}
